// src/oura/ui/OuraSidebar.tsx
//
// One sidebar for both sides of the product (D9): the staff business chrome
// and the merchant shell. 224px open, a 64px rail closed, the choice
// remembered, and always the rail below 1024px.
//
// Sticky rather than fixed: fixed would sit over the content the moment the
// viewport is shorter than the nav, and there are controls at its foot.
//
// THE RAIL DOES NOT SCROLL -- its list of pages does (#4026 C-FE-4). The whole
// rail used to be the scroll container, with `scrollbar-hide` on it, so on a
// viewport shorter than the nav the foot -- the bell and the account menu --
// sat below the fold INSIDE the rail, reachable only by scrolling a box with
// no scrollbar to say it scrolled. Scrolling the page did not help: the rail
// is `h-screen` and sticks. So the brand, the identity, the collapse control
// and the foot are pinned, and only the groups between them move.
//
// MOTION (Apple's fluid-interface rules, translated). Nothing on the rail
// moves when it opens or closes except the width: every icon, the business
// mark and the back chevron sit at the same x in both states, the group heads
// keep one height, and the words fade (out first, in after the width has
// started). The page you are on is ONE wash that slides to the new row rather
// than a background that jumps. A press shows at once. Both transitions are
// CSS, so a second click mid-way reverses from where it is, and both stop
// under prefers-reduced-motion.

import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { Building2Icon, ChevronLeftIcon, ChevronsUpDownIcon, PanelLeftIcon, type LucideIcon } from "lucide-react";
import { TooltipContent, TooltipProvider, TooltipRoot, TooltipTrigger } from "@component/tooltip";
import { cn } from "../lib/cn";
import { FOCUS_RING, NAV, TYPE } from "./tokens";
import { GLIDE, SETTLE, useGlideMark } from "./glide";
import { NARROW_VIEWPORT, useMediaQuery } from "./useMediaQuery";

export interface OuraSidebarItem {
    key: string;
    label: string;
    /** Takes the row's colour (NAV): grey, blue when active. */
    icon: LucideIcon;
    /** A drawn icon (an image asset) in place of `icon`, 17px square like the rest. */
    iconNode?: ReactNode;
    /** A small grey shortcut hint at the right, open only ("⌘J"). */
    kbd?: string;
    /** The row paints its own wash when active: it sits outside the groups the shared wash glides over. */
    ownWash?: boolean;
    active: boolean;
    /** A router link, or `onClick` for chrome that navigates through a handler. */
    to?: string;
    onClick?: () => void;
    /**
     * Amber count. `label` is the full phrase for the tooltip and screen readers ("3 need your answer").
     * `dot`: a blue dot instead of the number, for something new rather than something that needs them.
     */
    badge?: { count: number; label: string; dot?: boolean } | null;
    /**
     * What this page is FOR, shown on hover and on keyboard focus (#4026
     * C-FE-5). Nothing said what a sidebar item did before it was clicked.
     *
     * A Radix tooltip, not a native `title`: a `title` never appears on touch
     * and never on keyboard focus, which is the same rule #4025 set for the
     * Visibility chips. Open, an item with no hint gets no tooltip; closed,
     * every item gets one, which names it.
     */
    hint?: string;
}

/** A row with a drawn icon (`iconNode`) and no Lucide `icon`. */
export type OuraSidebarLead = Omit<OuraSidebarItem, "icon"> & { icon?: LucideIcon };

export interface OuraSidebarGroup {
    key: string;
    /** Sentence case. Collapsed, the label becomes the rule between groups. */
    label?: string;
    items: OuraSidebarItem[];
}

const RAIL_KEY = "\\";
const MOD_LABEL = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.userAgent) ? "⌘" : "Ctrl+";

/**
 * Words fade OUT at once and quickly, so they are gone before the width
 * reaches them, and fade IN after the width has started, so they never show
 * clipped. `overflow-hidden` + `min-w-0` lets the box shrink to nothing
 * closed instead of pushing past the rail.
 */
function fade(collapsed: boolean) {
    return cn(
        "min-w-0 overflow-hidden transition-opacity motion-reduce:transition-none",
        collapsed ? "opacity-0 duration-100" : "opacity-100 duration-200 delay-100",
    );
}

function readCollapsed(key: string): boolean {
    try {
        return window.localStorage.getItem(key) === "1";
    } catch {
        // Private windows and blocked site data both throw here. The sidebar
        // opens expanded, which is the useful default anyway.
        return false;
    }
}

/** The name, plus the hint when there is one. Shown right of the icon. */
function RailTip({ show, content, children }: { show: boolean; content: ReactNode; children: ReactNode }) {
    if (!show) return <>{children}</>;
    return (
        <TooltipRoot>
            {/* `asChild`, so the trigger is the link itself and the hover
                target is the whole row rather than a wrapper of its own size. */}
            <TooltipTrigger asChild>{children}</TooltipTrigger>
            {/* Grows out of the icon it names, not from its own centre. */}
            <TooltipContent side="right" sideOffset={8} className="origin-[var(--radix-tooltip-content-transform-origin)]">
                {content}
            </TooltipContent>
        </TooltipRoot>
    );
}

function NavItem({ item, collapsed }: { item: OuraSidebarLead; collapsed: boolean }) {
    const Icon = item.icon;
    const badge = item.badge && item.badge.count > 0 ? item.badge : null;
    const name = badge ? `${item.label}, ${badge.label}` : item.label;
    const cls = cn(
        // `pl-4` open AND closed: the icon's centre is the rail's centre, so
        // the icon does not move when the rail opens or closes. `relative`
        // keeps the row above the sliding wash.
        "relative flex h-[34px] w-full items-center gap-2.5 rounded-[9px] pl-4 pr-2.5 text-sm transition-colors",
        item.active ? NAV.active : NAV.rest,
        item.active && item.ownWash && NAV.activeWash,
        FOCUS_RING,
    );
    const body = (
        <>
            <span className="relative inline-flex shrink-0">
                {item.iconNode ?? (Icon && <Icon aria-hidden size={17} strokeWidth={2} />)}
                {/* Closed, the count moves onto the icon. */}
                {badge?.dot && (
                    <span
                        aria-hidden
                        className={cn(
                            "absolute -right-1 -top-0.5 h-2 w-2 rounded-full bg-blue-600 ring-2 ring-white transition-opacity motion-reduce:transition-none",
                            collapsed ? "opacity-100 delay-100 duration-200" : "opacity-0 duration-100",
                        )}
                    />
                )}
                {badge && !badge.dot && (
                    <span
                        aria-hidden
                        className={cn(
                            "absolute -right-[9px] -top-1.5 h-4 min-w-4 rounded-full bg-amber-50 px-1 text-center text-xs font-semibold leading-4 tabular-nums text-amber-800 ring-1 ring-amber-200 transition-opacity motion-reduce:transition-none",
                            collapsed ? "opacity-100 delay-100 duration-200" : "opacity-0 duration-100",
                        )}
                    >
                        {badge.count}
                    </span>
                )}
            </span>
            <span className={cn("flex flex-1 items-center gap-2.5", fade(collapsed))}>
                <span className="min-w-0 flex-1 truncate text-left">{item.label}</span>
                {item.kbd && (
                    <span aria-hidden className={cn(TYPE.tm, "shrink-0 font-normal text-gray-400")}>
                        {item.kbd}
                    </span>
                )}
                {badge?.dot && <span aria-hidden className="h-2 w-2 shrink-0 rounded-full bg-blue-600" />}
                {badge && !badge.dot && (
                    <span aria-hidden className="grid h-5 min-w-5 shrink-0 place-items-center rounded-full bg-amber-50 px-1.5 text-xs font-semibold tabular-nums text-amber-800">
                        {badge.count}
                    </span>
                )}
            </span>
        </>
    );
    const a11y = {
        // The tour points at rail items by view (#4062 MR-FE-10), and the
        // phone bar renders the same items, so one attribute finds either.
        "data-nav-view": item.key,
        "aria-current": item.active && !item.ownWash ? ("page" as const) : undefined,
        // A row that toggles something (Ask Oura) is pressed, not the current page.
        "aria-pressed": item.ownWash ? item.active : undefined,
        // The hint goes in the accessible name when there is one, so a screen
        // reader gets what the tooltip shows rather than only the label.
        "aria-label": item.hint ? `${name}. ${item.hint}` : collapsed || badge ? name : undefined,
    };
    const control = item.to ? (
        <Link to={item.to} className={cls} {...a11y}>
            {body}
        </Link>
    ) : (
        <button type="button" onClick={item.onClick} className={cls} {...a11y}>
            {body}
        </button>
    );
    // Closed there is no word on screen, so every icon names its page on
    // hover and on focus, hint or not.
    return (
        <RailTip
            show={collapsed || !!item.hint}
            content={
                collapsed ? (
                    <>
                        <span className="font-semibold">{item.label}</span>
                        {item.hint && <span className="mt-0.5 block">{item.hint}</span>}
                    </>
                ) : (
                    item.hint
                )
            }
        >
            {control}
        </RailTip>
    );
}

export interface OuraSidebarProps {
    ariaLabel: string;
    /** localStorage key for the collapsed choice, e.g. "oura.businessSidebar.collapsed". */
    storageKey: string;
    /** Staff: the way up to the list. The only route back, so it is always there. */
    backlink?: { to: string; label: string; title?: string };
    /** Merchant: the OURA logo above the identity, drawn for the current width. */
    brand?: (collapsed: boolean) => ReactNode;
    /** `name: null` while it loads (a placeholder bar). `action` sits beside the name, open only. */
    /**
     * `icon`: the business's own favicon, when there is one. A building is
     * drawn in its place while it is absent or fails to load.
     */
    identity: {
        name: string | null;
        sub?: ReactNode;
        action?: ReactNode;
        icon?: string | null;
        /** The name becomes a button with a chevron (staff: switch to another client). */
        switcher?: { onClick: () => void; title: string };
    };
    groups: OuraSidebarGroup[];
    /** One row above the first group, under the identity (staff: Ask Oura). */
    lead?: OuraSidebarLead;
    /** Controls at the bottom (bell, account), laid out for the current width. */
    foot?: (collapsed: boolean) => ReactNode;
    /** Hidden below 640px, where the merchant's bottom bar takes over. */
    phoneHidden?: boolean;
}

export function OuraSidebar({ ariaLabel, storageKey, backlink, brand, identity, groups, lead, foot, phoneHidden }: OuraSidebarProps) {
    const [chosenCollapsed, setChosenCollapsed] = useState(() => readCollapsed(storageKey));
    /**
     * Below 1024px the sidebar is a rail whatever was chosen.
     *
     * 224px of nav on a narrow screen left the content column so thin that
     * every table cell and sentence broke to two words a line. The stored
     * choice is about which layout someone likes on a desktop; it is not a
     * claim that a small screen has the room.
     */
    const narrow = useMediaQuery(NARROW_VIEWPORT);
    const collapsed = chosenCollapsed || narrow;

    const toggle = useCallback(() => {
        setChosenCollapsed((was) => {
            const next = !was;
            try {
                window.localStorage.setItem(storageKey, next ? "1" : "0");
            } catch {
                /* not worth failing a click over */
            }
            return next;
        });
    }, [storageKey]);

    // ⌘\ (Ctrl+\ elsewhere) opens and closes the rail, as in the apps people
    // already use. Not while the viewport forces the rail.
    useEffect(() => {
        if (narrow) return;
        const onKey = (e: KeyboardEvent) => {
            if ((e.metaKey || e.ctrlKey) && !e.altKey && !e.shiftKey && e.key === RAIL_KEY) {
                e.preventDefault();
                toggle();
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [narrow, toggle]);

    /** The wash under the page you are on: one element that glides to the new row (glide.ts). */
    const listRef = useRef<HTMLDivElement>(null);
    const activeKey = groups.flatMap((g) => g.items).find((it) => it.active)?.key ?? null;
    const { mark: wash, markRef: washRef } = useGlideMark(listRef, activeKey ? `[data-nav-view="${CSS.escape(activeKey)}"]` : null, storageKey, groups);

    const badgeMark = <BusinessMark icon={identity.icon ?? null} />;

    return (
        // One provider for the rail, so moving down the icons with one name
        // showing opens the next at once instead of waiting again.
        <TooltipProvider delayDuration={collapsed ? 100 : 300} skipDelayDuration={500}>
        <nav
            aria-label={ariaLabel}
            className={cn(
                // `overflow-x-hidden` + `whitespace-nowrap`: while the width
                // animates, a label is wider than the nav it sits in, and
                // without these it spills over the content or wraps. The
                // padding is the same in both states, so only the width moves.
                "sticky top-0 h-screen shrink-0 flex-col self-start overflow-x-hidden whitespace-nowrap bg-white px-2 py-4 transition-[width] duration-300 motion-reduce:transition-none",
                SETTLE,
                phoneHidden ? "hidden sm:flex" : "flex",
                collapsed ? "w-16" : "w-56",
            )}
        >
            {/* Same place open and closed, like every icon below it. */}
            {brand && <div className="mb-4 flex pl-2">{brand(collapsed)}</div>}

            {backlink && (
                <Link
                    to={backlink.to}
                    title={backlink.title}
                    className={cn(
                        TYPE.tc,
                        // The chevron's centre is the icons' centre, open and closed.
                        "mb-3 flex items-center gap-1 rounded-md py-1 pl-[18px] font-medium text-gray-500 transition-colors hover:text-gray-900",
                        FOCUS_RING,
                    )}
                >
                    <ChevronLeftIcon aria-hidden size={14} className="shrink-0" />
                    <span className={fade(collapsed)}>{backlink.label}</span>
                </Link>
            )}

            <div className="mb-3 flex items-center gap-2.5 pl-2" title={collapsed ? identity.name ?? undefined : undefined}>
                {badgeMark}
                <div className={cn("flex-1", fade(collapsed))}>
                    <div className="flex items-center gap-1.5">
                        {identity.name === null ? (
                            <span className="h-4 w-28 animate-pulse rounded bg-gray-100" />
                        ) : identity.switcher ? (
                            <button
                                type="button"
                                onClick={identity.switcher.onClick}
                                title={identity.switcher.title}
                                aria-label={identity.switcher.title}
                                className={cn(FOCUS_RING, "-mx-1 flex min-w-0 items-center gap-1 rounded-md px-1 text-left hover:bg-gray-100")}
                            >
                                <span data-oura-identity className="truncate text-sm font-semibold text-gray-900">{identity.name}</span>
                                <ChevronsUpDownIcon aria-hidden size={13} className="shrink-0 text-gray-400" />
                            </button>
                        ) : (
                            <span data-oura-identity className="truncate text-sm font-semibold text-gray-900">{identity.name}</span>
                        )}
                        {!collapsed && identity.action}
                    </div>
                    {identity.sub && <div className={cn(TYPE.tm, "truncate text-gray-500")}>{identity.sub}</div>}
                </div>
            </div>

            {/* The ONLY thing that scrolls. `min-h-0` is what makes it work:
                without it a flex child refuses to shrink below its content and
                the overflow moves back up to the rail. `scrollbar-hide` is
                safe here because what is above and below it never leaves the
                screen, so nothing is hidden behind an invisible scrollbar.
                `relative`: the sliding wash is measured against it. */}
            <div ref={listRef} className="relative min-h-0 flex-1 overflow-y-auto overflow-x-hidden scrollbar-hide">
            {wash && (
                // Full width rather than the measured one, so the wash follows
                // the rail's own width while it opens and closes.
                <span
                    ref={washRef}
                    aria-hidden
                    className={cn(GLIDE, "inset-x-0 rounded-[9px]", NAV.activeWash)}
                    style={{ height: wash.h, transform: `translate(${wash.x}px, ${wash.y}px)` }}
                />
            )}
            {lead && (
                <ul aria-label={lead.label} className="list-plain m-0 space-y-0.5 p-0">
                    <li>
                        <NavItem item={lead} collapsed={collapsed} />
                    </li>
                </ul>
            )}
            {groups.map((group, i) => (
                <div key={group.key}>
                    {/* One height open and closed, so the rows below never
                        jump: the word fades out and a rule fades in. */}
                    {group.label && (
                        <div aria-hidden={collapsed || undefined} className="relative mb-1 mt-4 h-[18px]">
                            <p className={cn(TYPE.lab, "m-0 pl-4", fade(collapsed))}>{group.label}</p>
                            {i > 0 && (
                                <span
                                    aria-hidden
                                    className={cn(
                                        "absolute inset-x-3 top-1/2 border-t border-gray-200 transition-opacity motion-reduce:transition-none",
                                        collapsed ? "opacity-100 delay-100 duration-200" : "opacity-0 duration-100",
                                    )}
                                />
                            )}
                        </div>
                    )}
                    <ul aria-label={group.label} className={cn("list-plain m-0 space-y-0.5 p-0", !group.label && "mt-2")}>
                        {group.items.map((item) => (
                            <li key={item.key}>
                                <NavItem item={item} collapsed={collapsed} />
                            </li>
                        ))}
                    </ul>
                </div>
            ))}

            </div>

            {/* Hidden while the viewport forces the answer: a control that
                cannot change what it names is worse than no control. */}
            {!narrow && (
                <RailTip
                    show
                    content={
                        <>
                            {collapsed ? "Expand sidebar" : "Collapse sidebar"}
                            <span className="ml-2 opacity-70">{MOD_LABEL}\</span>
                        </>
                    }
                >
                    <button
                        type="button"
                        onClick={toggle}
                        aria-expanded={!collapsed}
                        aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
                        aria-keyshortcuts="Meta+Backslash Control+Backslash"
                        className={cn(
                            "relative mt-3 flex h-[34px] w-full shrink-0 items-center gap-2.5 rounded-[9px] pl-4 pr-2.5 text-sm text-gray-400 transition-colors hover:bg-gray-50 hover:text-gray-600 active:bg-gray-100 active:duration-0",
                            FOCUS_RING,
                        )}
                    >
                        <PanelLeftIcon aria-hidden size={17} className="shrink-0" />
                        <span className={fade(collapsed)}>Collapse</span>
                    </button>
                </RailTip>
            )}

            {/* `shrink-0`, and no `mt-auto`: the scroll area above already
                takes every spare pixel, and mt-auto on a sibling of a
                `flex-1` child fights it. The rule sets the bell and the
                account apart from the navigation, so Collapse reads as the
                last navigation row instead of floating between the two. */}
            {foot && <div className="mt-3 shrink-0 border-t border-gray-100 pt-3">{foot(collapsed)}</div>}
        </nav>
        </TooltipProvider>
    );
}

/**
 * The business's mark beside its name: its favicon on white when there is
 * one, else a building, not initials: most business names are several words,
 * and two letters from them meant nothing as often as not. A favicon that
 * fails to load (gone, blocked, not an image) falls back to the building.
 */
function BusinessMark({ icon }: { icon: string | null }) {
    const [broken, setBroken] = useState<string | null>(null);
    const show = icon && broken !== icon;
    return (
        <span
            aria-hidden
            className={cn("grid h-8 w-8 shrink-0 place-items-center overflow-hidden rounded-lg", show ? "bg-white ring-1 ring-inset ring-black/[0.06]" : NAV.mark)}
        >
            {show ? (
                <img src={icon} alt="" className="h-5 w-5 object-contain" referrerPolicy="no-referrer" onError={() => setBroken(icon)} />
            ) : (
                <Building2Icon size={16} />
            )}
        </span>
    );
}
